import { RangeAddress } from '@shared/excel/range-address';
import { getRowIndexesToRead } from '@shared/execution/executors/common';
import { WorkbookApi, WorkbookApiFactory } from '@shared/execution/workbook-api';
import { EventBus } from '@shared/infrastructure/app-event-bus';
import { ProviderClient, ProviderEvents, ProviderFactory, ProviderRemoteChunkEvent } from '@shared/providers/provider';
import {
  DeleteRecordError,
  isDeleteRecordError,
  isMetadata,
  MetadataResult,
  STREAM_METADATA,
  UpdateResult,
} from '@shared/providers/provider-transport';

import { Flowstep } from '@shared/types/flowstep';
import { CancelError, CancelToken } from '@shared/utils/cancelToken';
import { NoDataError, RangeType, ResultDataError } from '@shared/utils/flowstep';
import dayjs from 'dayjs';
import { CellAddressPickerValue } from 'frontend/excel365-addin/src/v2/components/base/CellAddressPicker/types';
import Highland from 'highland';
import {
  ExecutorActionError,
  ExecutorActionEvent,
  ExecutorActionFinished,
  ExecutorActionStarted,
  ExecutorCancelled,
} from './events';
import Executor from './index';

void Highland;

interface Dependencies {
  providerFactory: ProviderFactory;
  workbookApiFactory: WorkbookApiFactory;
  eventBus: EventBus;
}

class DeleteExecutor extends Executor {
  private providerFactory: ProviderFactory;
  private workbookApiFactory: WorkbookApiFactory;
  private eventBus: EventBus;

  constructor(deps: Dependencies) {
    super();
    this.eventBus = deps.eventBus;
    this.providerFactory = deps.providerFactory;
    this.workbookApiFactory = deps.workbookApiFactory;
  }

  private timestamp: string | null = null;
  private resultErrors: DeleteRecordError[] = [];
  flowstep!: Flowstep;
  worksheet: any;
  private workbookApi!: WorkbookApi<any>;
  private provider!: ProviderClient;
  private progressCounters = {
    total: 0,
    read: 0,
    processed: 0,
    populated: 0,
  };

  init(flowstep: Flowstep, context: any): void {
    this.flowstep = flowstep;
    this.workbookApi = this.workbookApiFactory(context);
    this.provider = this.providerFactory.getProviderClient(flowstep.provider, context);
    this.registerListeners();
  }

  private registerListeners() {
    this.eventBus.addListener<ProviderRemoteChunkEvent>(ProviderEvents.REMOTE_API_CHUNK, (event) => {
      this.progressCounters.processed += event.chunkSize;
      this.eventBus.notify(
        new ExecutorActionEvent(`Deleting`, `${this.progressCounters.processed} out of ${this.progressCounters.total}`),
      );
    });
  }

  private unregisterListeners() {
    this.eventBus.clearListeners(ProviderEvents.REMOTE_API_CHUNK);
  }

  async execute(cancelToken?: CancelToken) {
    try {
      this.eventBus.notify(new ExecutorActionStarted());
      const { worksheetId, target, onlyVisibleRows, rangeToApply, showOperationResultsTable } = this.flowstep.options;
      const { idColumn } = this.flowstep.providerOptions;

      this.eventBus.notify(new ExecutorActionEvent('Detecting data range'));
      this.worksheet = await this.workbookApi.getWorksheet?.(worksheetId);
      const range = await this.workbookApi.detectRangeByCell(worksheetId, target);

      if (!range) {
        throw new NoDataError('Could not find the range for delete');
      }

      const headers = await this.workbookApi.getColumnHeaders(range);

      const idColumnIndex = headers.findIndex((header) => header === idColumn);

      if (idColumnIndex === -1) {
        throw new Error(`ID column '${idColumn}' not found in the range`);
      }

      const providerMeta = this.providerFactory.getProviderMeta(this.flowstep.provider);
      const options = {
        visibleOnly: onlyVisibleRows,
        columnsToRead: [idColumnIndex],
        selectedOnly: rangeToApply.type === RangeType.Selected,
        rowSize: providerMeta.getIdFieldLength(),
        specifiedRowNumbers: await getRowIndexesToRead(this.flowstep, range, this.workbookApi),
      };

      this.progressCounters.total = await this.workbookApi.calcRowCountToRead(range, options);
      const dataStream = (await this.workbookApi.readRangeData(range, options)).tap(() => {
        this.progressCounters.read++;
        if (this.progressCounters.read % 1000 === 0) {
          this.eventBus.notify(
            new ExecutorActionEvent(
              'Reading data',
              `${this.progressCounters.read} out of ${this.progressCounters.total}`,
            ),
          );
        }
      });

      const providerStream = await this.provider.deleteFlowstepData(this.flowstep, dataStream, cancelToken);

      let resultStream = this.processMetadataStream(providerStream);

      resultStream = this.collectResultErrors(resultStream);
      let resultRange;

      resultStream = resultStream
        .tap(() => {
          this.progressCounters.populated++;
          if (this.progressCounters.populated % 1000 === 0) {
            this.eventBus.notify(
              new ExecutorActionEvent(
                `Populating results`,
                `${this.progressCounters.populated} out of ${this.progressCounters.total}`,
              ),
            );
          }
        })
        .map((result) => this.formatResult(result));

      resultRange = await this.workbookApi.writeRangeResult(range, resultStream, {
        writeRowStates: showOperationResultsTable,
      });

      if (cancelToken?.isCanceled()) {
        const reason = cancelToken.reason || 'Execution cancelled';
        this.eventBus.notify(new ExecutorCancelled(reason));
        return { error: new CancelError(reason) };
      }

      await this.throwResultErrors(range, resultRange);

      this.eventBus.notify(
        new ExecutorActionFinished('Execution finished', { resultSuccessRange: resultRange, warnings: [] }),
      );
      return { warnings: [] };
    } catch (error: unknown) {
      if (error instanceof NoDataError) {
        this.eventBus.notify(new ExecutorActionFinished('Execution finished with warnings', { warnings: [error] }));
        return { warnings: [] };
      }

      if (error instanceof CancelError) {
        return { error };
      }

      this.eventBus.notify(new ExecutorActionError(error as Error));
      return { warnings: [], error };
    } finally {
      this.unregisterListeners();
      if (this.flowstep.options.timestampCell) {
        await this.setLastRunTimestamp();
      }
    }
  }

  private processMetadataStream(stream: Highland.Stream<any>): Highland.Stream<any> {
    return stream.filter((item: any) => {
      if (!isMetadata(item)) {
        return true;
      }

      this.processMetaItem(item);
      return false;
    });
  }

  private processMetaItem(item: MetadataResult): void {
    if (item.type === STREAM_METADATA.TIMESTAMP) {
      this.timestamp = item.payload.timestamp;
    }
  }

  private collectResultErrors(stream: Highland.Stream<any>) {
    return stream.tap((item: any) => {
      if (isDeleteRecordError(item)) {
        this.resultErrors.push(item);
      }
    });
  }

  private async throwResultErrors(tableRange: any, resultRange?: any) {
    if (this.resultErrors.length === 0) {
      return;
    }
    const { address } = await this.workbookApi.getRangeAddressInfo(tableRange);
    const rangeAddress = RangeAddress.fromString(address);
    const messages = this.resultErrors.map(
      (error) => `Row${rangeAddress.startRow + error.__rowIndex}: Error deleting ${error.status}`,
    );

    throw new ResultDataError(messages, resultRange);
  }

  private async setLastRunTimestamp() {
    if (!this.timestamp) {
      return;
    }
    const { worksheetId, address } = this.flowstep.options.timestampCell as CellAddressPickerValue;
    const range = await this.workbookApi.getRangeByAddress(`${worksheetId}!${address}`);
    if (!range) {
      return;
    }
    await this.workbookApi.writeRangeValue(range, [[this.timestamp]]);
  }

  private formatResult(result: UpdateResult) {
    const timestamp = this.timestamp ? dayjs(this.timestamp).format('YYYY-MM-DD HH:mm:ss') : '';
    const status = result.status === 'success' ? 'Successfully deleted' : result.status.replace('\n', ' ');
    return { ...result, status, timestamp };
  }
}

export default DeleteExecutor;
